import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Upload } from 'lucide-react';
import { useRef, type ChangeEvent } from 'react';

import { DOCUMENT_ATTACHMENT_TYPE } from '@carsfinders/contracts';

import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { getDocumentViewUrl } from '../../documents/api';
import { fetchUserDocuments, uploadUserDocument } from '../api';
import { type User } from '../types';
import { formatShortDate } from '../utils';

/** What the file input offers — the same set the API's file validator accepts. */
const ACCEPTED_FILES = 'application/pdf,image/jpeg,image/png';

/**
 * «Документи клієнта» — every file on the client's record, and ONE action:
 * upload a file from this computer (owner, 2026-10-12).
 *
 * The operator scans or photographs a contract, an agreement or a payment slip
 * and attaches it; it is stored as an attachment (`DOCUMENT_ATTACHMENT_TYPE`),
 * which the API keeps out of KYC — no «in review» flip, no review queue. The
 * client's own identity documents appear in the same list.
 *
 * Replaces the old identity-document form (eleven typed fields under a single
 * scan): it could hold only one document, and its upload never reached the
 * API's checks — it sent no `documentType`, which the API answers with a 400.
 */
export function UserDocumentsTab({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const userId = user._id;
  const inputRef = useRef<HTMLInputElement>(null);

  const documentsQuery = useQuery({
    queryKey: ['user', userId, 'documents'],
    queryFn: () => fetchUserDocuments(userId as string),
    enabled: !!userId,
  });
  const documents = documentsQuery.data ?? [];

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadUserDocument(userId as string, file),
    onSuccess: () => {
      toast.success(t('clients.detail.documents.uploaded'));
      void queryClient.invalidateQueries({ queryKey: ['user', userId, 'documents'] });
    },
    onError: (error) =>
      toast.error(
        error instanceof Error && error.message
          ? `${t('clients.detail.documents.uploadFailed')}: ${error.message}`
          : t('clients.detail.documents.uploadFailed'),
      ),
  });

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset first, so picking the same file twice still fires a change.
    event.target.value = '';
    if (file && userId) uploadMutation.mutate(file);
  };

  const openDocument = async (documentId: string) => {
    try {
      const { presignedUrl } = await getDocumentViewUrl(documentId);
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('clients.detail.documents.openFailed'));
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-cf-lg border border-cf-line bg-cf-surface p-4 lg:gap-4 lg:px-6 lg:py-[22px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base leading-[21px] font-semibold tracking-[-0.02em] lg:text-[18px] lg:leading-[23px]">
            {t('clients.detail.documents.title')}
          </h2>
          <p className="mt-0.5 text-xs text-cf-ink-subtle">{t('clients.detail.documents.hint')}</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_FILES}
          onChange={handleFileChange}
          className="hidden"
          data-testid="client-documents-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploadMutation.isPending || !userId}
          data-testid="client-documents-upload"
          className="flex h-10 items-center gap-2 rounded-cf-md bg-cf-brand px-4 text-sm font-semibold text-white hover:bg-cf-brand-hover disabled:opacity-50"
        >
          <Upload size={16} strokeWidth={1.75} aria-hidden />
          {uploadMutation.isPending
            ? t('clients.detail.documents.uploading')
            : t('clients.detail.documents.upload')}
        </button>
      </div>

      {documents.length === 0 ? (
        <p className="m-0 text-sm text-cf-ink-muted" data-testid="client-documents-empty">
          {t('clients.detail.documents.empty')}
        </p>
      ) : (
        <ul className="m-0 list-none divide-y divide-cf-line-soft p-0" data-testid="client-documents-list">
          {documents.map((doc) => (
            <li key={doc._id ?? doc.fileName} className="flex items-center gap-3 py-2.5">
              <FileText size={18} strokeWidth={1.75} className="shrink-0 text-cf-ink-subtle" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-cf-ink">{doc.fileName ?? '—'}</div>
                <div className="text-xs text-cf-ink-subtle">
                  {doc.documentType === DOCUMENT_ATTACHMENT_TYPE
                    ? t('clients.detail.documents.attachment')
                    : t('clients.detail.documents.identity')}
                  {doc.uploadedAt ? ` · ${formatShortDate(doc.uploadedAt)}` : ''}
                </div>
              </div>
              {doc._id && (
                <button
                  type="button"
                  onClick={() => void openDocument(doc._id as string)}
                  className="h-9 shrink-0 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[13px] font-semibold hover:border-cf-ink"
                >
                  {t('clients.detail.documents.open')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
